import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  Banknote,
  CalendarDays,
  ClipboardList,
  CheckCircle,
  XCircle,
  BellOff,
  X,
} from "lucide-react";
import { clsx } from "clsx";
import type { AppNotification } from "../../lib/notificationApi";

interface Props {
  notifications: AppNotification[];
  unreadCount: number;
  onMarkRead: (notif: AppNotification) => void;
  onMarkAllRead: () => void;
  onClose: () => void;
}

function notifIcon(type: string) {
  if (type.startsWith("LOAN")) return <Banknote className="h-4 w-4" />;
  if (type.startsWith("LEAVE")) return <CalendarDays className="h-4 w-4" />;
  if (type.startsWith("TASK")) return <ClipboardList className="h-4 w-4" />;
  if (type.includes("APPROVED")) return <CheckCircle className="h-4 w-4" />;
  if (type.includes("REJECTED")) return <XCircle className="h-4 w-4" />;
  return <ClipboardList className="h-4 w-4" />;
}

function iconColor(type: string) {
  if (type.includes("APPROVED") || type.includes("RELEASED")) return "text-emerald-600 bg-emerald-50";
  if (type.includes("REJECTED")) return "text-red-500 bg-red-50";
  if (type.startsWith("LOAN")) return "text-[#2447d9] bg-blue-50";
  if (type.startsWith("LEAVE")) return "text-violet-600 bg-violet-50";
  if (type.startsWith("TASK")) return "text-amber-600 bg-amber-50";
  return "text-[#64748b] bg-[#f1f5f9]";
}

function NotifHeader({
  unreadCount,
  onMarkAllRead,
  onClose,
  showClose = false,
}: {
  unreadCount: number;
  onMarkAllRead: () => void;
  onClose: () => void;
  showClose?: boolean;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-[#e2e8f0] shrink-0">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-[#0f172a]">Notifications</span>
        {unreadCount > 0 && (
          <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-[#2447d9] text-white text-[10px] font-bold">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {unreadCount > 0 && (
          <button
            onClick={onMarkAllRead}
            className="text-xs text-[#2447d9] hover:underline font-medium"
          >
            Mark all read
          </button>
        )}
        {showClose && (
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] transition-colors"
            aria-label="Close notifications"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function NotifList({
  notifications,
  onMarkRead,
  onClose,
}: {
  notifications: AppNotification[];
  onMarkRead: (n: AppNotification) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  function handleClick(notif: AppNotification) {
    onMarkRead(notif);
    onClose();
    navigate(notif.link);
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-[#94a3b8]">
        <BellOff className="h-8 w-8" />
        <p className="text-sm">No notifications yet</p>
      </div>
    );
  }

  return (
    <>
      {notifications.map((notif) => (
        <button
          key={notif.notifId}
          onClick={() => handleClick(notif)}
          className={clsx(
            "w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[#f8fafc] border-b border-[#f1f5f9] last:border-0",
            !notif.read && "bg-blue-50/40",
          )}
        >
          <span
            className={clsx(
              "flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full mt-0.5",
              iconColor(notif.type),
            )}
          >
            {notifIcon(notif.type)}
          </span>
          <div className="flex-1 min-w-0">
            <p
              className={clsx(
                "text-sm leading-snug truncate",
                notif.read ? "text-[#475569] font-normal" : "text-[#0f172a] font-semibold",
              )}
            >
              {notif.title}
            </p>
            <p className="text-xs text-[#64748b] mt-0.5 line-clamp-2 leading-snug">
              {notif.body}
            </p>
            <p className="text-[10px] text-[#94a3b8] mt-1">
              {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
            </p>
          </div>
          {!notif.read && (
            <span className="flex-shrink-0 h-2 w-2 rounded-full bg-[#2447d9] mt-1.5" aria-hidden="true" />
          )}
        </button>
      ))}
    </>
  );
}

export function NotificationDrawer({
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onClose,
}: Props) {
  return (
    <>
      {/* ── Mobile: full-width bottom sheet ── */}
      <div className="md:hidden">
        {/* Backdrop — fixed covers full viewport; inside containerRef so outside-click handler ignores it */}
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
        {/* Sheet */}
        <div
          className="fixed bottom-0 left-0 right-0 z-50 flex flex-col bg-white rounded-t-2xl shadow-[0_-8px_32px_0_rgb(0_0_0/0.15)] sheet-enter"
          style={{ maxHeight: "85dvh", paddingBottom: "env(safe-area-inset-bottom)" }}
          role="dialog"
          aria-label="Notifications"
          aria-modal="true"
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2 shrink-0">
            <div className="w-10 h-1 rounded-full bg-[#cbd5e1]" />
          </div>
          <NotifHeader
            unreadCount={unreadCount}
            onMarkAllRead={onMarkAllRead}
            onClose={onClose}
            showClose
          />
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <NotifList
              notifications={notifications}
              onMarkRead={onMarkRead}
              onClose={onClose}
            />
          </div>
        </div>
      </div>

      {/* ── Desktop: anchored dropdown ── */}
      <div className="hidden md:block absolute right-0 top-full mt-2 w-[360px] bg-white rounded-xl border border-[#e2e8f0] shadow-[0_8px_24px_0_rgb(0_0_0/0.12)] z-50 overflow-hidden">
        <NotifHeader
          unreadCount={unreadCount}
          onMarkAllRead={onMarkAllRead}
          onClose={onClose}
        />
        <div className="max-h-[420px] overflow-y-auto">
          <NotifList
            notifications={notifications}
            onMarkRead={onMarkRead}
            onClose={onClose}
          />
        </div>
      </div>
    </>
  );
}
